import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, View } from './components/base_components';
import { BusyOverlay, useBusy } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import BoxPropList from './components/box_prop_list';
import TitleActionLine from './components/title_action_line';
import VolumeTabBar from './components/volume_tab_bar';

import OrgStore from './stores/org_store';
import UserStore from './stores/user_store';
import VolumeStore from './stores/volume_store';

import { byteFormat } from './tools/util';

const styles = StyleSheet.create({
  volumeSettingScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  tabBar: {
    marginLeft: '2rem',
    marginRight: '2rem',
  },
});

export default function VolumeSettingScreen(props) {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org = UserStore.useOrg();
  const plan = OrgStore.usePlan(org?.org_id);
  const volume_id = parseInt(params.volume_id ?? '0', 10) || 0;
  const volume = VolumeStore.useVolume(org?.org_id, volume_id);
  const [updates, setUpdates] = React.useState<any>({});

  const live_start = volume?.live_expire_days > 0 ? 'Enabled' : 'Disabled';
  const dead_start = volume?.dead_expire_days > 0 ? 'Enabled' : 'Disabled';
  const live_enabled = updates.live_enabled ?? live_start;
  const dead_enabled = updates.dead_enabled ?? dead_start;

  React.useEffect(() => {
    VolumeStore.fetch(org?.org_id);
    OrgStore.fetch(org?.org_id);
  }, [org?.org_id]);

  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = {
        org_id: org?.org_id,
        volume_id,
      };
      if (updates.volume_name !== undefined) {
        opts.volume_name = updates.volume_name;
      }
      if (updates.is_disabled !== undefined) {
        opts.is_disabled = updates.is_disabled === 'Disabled';
      }
      if (updates.min_expire_size !== undefined) {
        opts.min_expire_size = updates.min_expire_size;
      }
      const changed_live = _daysChanged(
        updates.live_enabled,
        updates.live_expire_days,
        plan?.default_dead_expire_days
      );
      if (changed_live !== undefined) {
        opts.live_expire_days = changed_live;
      }
      const changed_dead = _daysChanged(
        updates.dead_enabled,
        updates.dead_expire_days,
        plan?.default_dead_expire_days
      );
      if (changed_dead !== undefined) {
        opts.dead_expire_days = changed_dead;
      }

      VolumeStore.updateVolume(opts, (err, body) => {
        clearBusy();
        if (err === 400 && opts.volume_name) {
          window.alert(
            'Sorry, your volume name is illegal.  The name must between 3 and 64 characters, must start and end with a letter or number, and may only contain the characters A-Z, a-z, 0-9 and space, underscore, and dash.'
          );
        } else if (err === 'max_volume_count') {
          window.alert('Sorry, your plan limit for volumes has been reached.');
        } else if (err === 'max_live_expire_days') {
          window.alert(
            `Sorry, your plan limits your active file expiration to ${body?.limit} days.`
          );
        } else if (err === 'max_dead_expire_days') {
          window.alert(
            `Sorry, your plan limits your inactive file expiration to ${body?.limit} days.`
          );
        } else if (err === 'max_min_expire_size') {
          window.alert(
            `Sorry, your plan limits your minimum expire size to ${body?.limit}.`
          );
        } else if (err === 402) {
          window.alert('Sorry, your plan does not allow this change.');
        } else if (err) {
          window.alert('Failed to update volume.');
        } else {
          complete?.();
        }
      });
    }
  }

  return (
    <View style={styles.volumeSettingScreen}>
      <Breadcrumb
        list={[{ text: 'Volumes', url: '/volume' }, volume?.volume_name]}
      />
      <TitleActionLine title={volume?.volume_name} />
      <VolumeTabBar style={styles.tabBar} volumeId={volume_id} />
      <BoxPropList
        title='Volume Settings'
        editable={org?.is_admin}
        updates={updates}
        onChange={setUpdates}
        onSave={_onSave}
        columns={[
          [
            {
              key: 'volume_name',
              label: 'Volume Name',
              value: volume?.volume_name,
              input: {
                placeholder: 'Volume Name',
              },
            },
            {
              key: 'is_disabled',
              label: 'Status',
              value: volume?.is_disabled ? 'Disabled' : 'Enabled',
              radio: {
                options: ['Enabled', 'Disabled'],
              },
            },
            {
              key: 'min_expire_size',
              label: 'Cloud Minimum Expire Size',
              detail:
                'Files smaller than this size will be retained in cloud storage forever to speed cloning and version recovery.',
              value: volume?.min_expire_size,
              viewValue: byteFormat(volume?.min_expire_size),
              input: {
                placeholder: 'bytes',
              },
            },
            {
              key: 'live_enabled',
              label: 'Cloud Active File Expiration',
              detail:
                'Remove active files from cloud storage after the set number of days. Your billing may be based on cloud storage usage depending on your plan.',
              value: live_start,
              viewValue: _viewExpire(volume?.live_expire_days),
              radio: {
                options: ['Enabled', 'Disabled'],
              },
            },
            {
              key: 'live_expire_days',
              value:
                volume?.live_expire_days || plan?.default_live_expire_days || 1,
              valueSuffix: ' days',
              editOnly: true,
              input: {
                disabled: live_enabled === 'Disabled',
                type: 'number',
                min: 1,
                max: 365,
                suffix: 'days',
              },
            },
            {
              key: 'dead_enabled',
              label: 'Cloud Inactive Version Expiration',
              detail:
                'Remove inactive versions from cloud storage after the set number of days.',
              value: dead_start,
              viewValue: _viewExpire(volume?.dead_expire_days),
              radio: {
                options: ['Enabled', 'Disabled'],
              },
            },
            {
              key: 'dead_expire_days',
              value:
                volume?.dead_expire_days || plan?.default_dead_expire_days || 1,
              valueSuffix: ' days',
              editOnly: true,
              input: {
                disabled: dead_enabled === 'Disabled',
                type: 'number',
                min: 1,
                max: 365,
                suffix: 'days',
              },
            },
          ],
        ]}
      />
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
function _viewExpire(days: number) {
  return days === 0 ? 'No Expiration' : `${days} days`;
}
function _daysChanged(enabled, days, def) {
  let ret;
  if (enabled === 'Disabled') {
    ret = 0;
  } else if (enabled === 'Enabled' || days) {
    ret = days || def;
  }
  return ret;
}
